import React from 'react';
import { BarChart, Bar, LineChart, Line, ComposedChart, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { exportToCSV } from '../../../services/exportService';

const ProfitDrivers = ({ onShowEvidence }) => {
  const profitByProduct = [
    { product: "Enterprise Solutions", revenue: 4250000, cost: 2800000, profit: 1450000, margin: 34.1 },
    { product: "Cloud Services", revenue: 3800000, cost: 2500000, profit: 1300000, margin: 34.2 },
    { product: "Consulting", revenue: 2900000, cost: 2100000, profit: 800000, margin: 27.6 },
    { product: "Support & Maintenance", revenue: 2100000, cost: 1600000, profit: 500000, margin: 23.8 },
    { product: "Training Services", revenue: 1450000, cost: 1300000, profit: 150000, margin: 10.3 }
  ];

  const profitByProgram = [
    { program: "Enterprise Solutions", revenue: 4250000, cost: 2850000, profit: 1400000, margin: 32.9 },
    { program: "Cloud Services", revenue: 3800000, cost: 2340000, profit: 1460000, margin: 38.4 },
    { program: "Consulting", revenue: 2900000, cost: 1680000, profit: 1220000, margin: 42.1 },
    { program: "Support & Maintenance", revenue: 2100000, cost: 1120000, profit: 980000, margin: 46.7 },
    { program: "Training Services", revenue: 1450000, cost: 760000, profit: 690000, margin: 47.6 }
  ];

  const profitTrend = [
    { month: "Aug 2025", profit: 4650000, margin: 37.2, target: 4500000 },
    { month: "Sep 2025", profit: 5080000, margin: 38.5, target: 4750000 },
    { month: "Oct 2025", profit: 5450000, margin: 39.5, target: 5000000 },
    { month: "Nov 2025", profit: 5520000, margin: 39.1, target: 5250000 },
    { month: "Dec 2025", profit: 5850000, margin: 40.3, target: 5500000 },
    { month: "Jan 2026", profit: 5750000, margin: 39.7, target: 5750000 }
  ];

  const revenueVsCost = [
    { month: "Aug 2025", revenue: 12500000, cost: 7850000 },
    { month: "Sep 2025", revenue: 13200000, cost: 8120000 },
    { month: "Oct 2025", revenue: 13800000, cost: 8350000 },
    { month: "Nov 2025", revenue: 14100000, cost: 8580000 },
    { month: "Dec 2025", revenue: 14500000, cost: 8650000 },
    { month: "Jan 2026", revenue: 14500000, cost: 8750000 }
  ];

  const profitMetrics = {
    totalProfit: 5750000,
    profitMargin: 39.7,
    profitGrowth: 8.5,
    targetAchievement: 100.0
  };

  const profitabilityInsights = [
    {
      insight: "Highest Margin Programs",
      description: "Training Services and Support & Maintenance show the strongest profit margins above 45%",
      recommendation: "Consider expanding these high-margin service offerings and allocating more resources",
      impact: "Positive"
    },
    {
      insight: "Revenue-Cost Gap Widening",
      description: "Revenue growth (3.2%) is outpacing cost growth (1.2%), improving overall profitability",
      recommendation: "Maintain cost discipline while continuing revenue expansion initiatives",
      impact: "Positive"
    },
    {
      insight: "Enterprise Solutions Opportunity",
      description: "Largest revenue generator but lower margin (32.9%) compared to other programs",
      recommendation: "Optimize delivery costs and pricing strategy to improve margin without sacrificing volume",
      impact: "Neutral"
    },
    {
      insight: "Consistent Margin Performance",
      description: "Profit margin has remained stable between 37-40% over the past 6 months",
      recommendation: "Set aggressive targets for margin expansion through operational efficiency",
      impact: "Neutral"
    }
  ];

  const getImpactColor = (impact) => {
    if (impact === 'Positive') return 'var(--color-success)';
    if (impact === 'Negative') return 'var(--color-error)';
    return 'var(--color-secondary)';
  };

  const getImpactIcon = (impact) => {
    if (impact === 'Positive') return 'TrendingUp';
    if (impact === 'Negative') return 'TrendingDown';
    return 'Minus';
  };

  const handleExportCSV = () => {
    const profitData = profitByProduct?.map(item => ({
      Product: item?.product,
      Revenue: `$${(item?.revenue / 1000000)?.toFixed(2)}M`,
      Cost: `$${(item?.cost / 1000000)?.toFixed(2)}M`,
      Profit: `$${(item?.profit / 1000000)?.toFixed(2)}M`,
      'Margin %': item?.margin
    }));

    exportToCSV({
      title: 'Profit_Drivers_By_Product',
      data: profitData
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">Profit Drivers</h2>
            <p className="text-sm text-muted-foreground font-caption">Profitability analysis and margin optimization</p>
          </div>
        </div>
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          iconName="Download"
        >
          Export CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Total Profit</span>
            <Icon name="DollarSign" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">${(profitMetrics?.totalProfit / 1000000)?.toFixed(2)}M</p>
          <p className="text-xs text-success font-caption mt-1">+{profitMetrics?.profitGrowth}% vs last month</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Profit Margin</span>
            <Icon name="Percent" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">{profitMetrics?.profitMargin}%</p>
          <p className="text-xs text-success font-caption mt-1">+0.6% improvement</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Target Achievement</span>
            <Icon name="Target" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">{profitMetrics?.targetAchievement}%</p>
          <p className="text-xs text-success font-caption mt-1">On target</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Best Margin</span>
            <Icon name="Award" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">47.6%</p>
          <p className="text-xs text-muted-foreground font-caption mt-1">Training Services</p>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Profit by Program</h3>
        <div className="h-80 md:h-96" aria-label="Profit by Program Bar Chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={profitByProgram}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="program" 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={100}
              />
              <YAxis 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-popover)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
                formatter={(value, name) => {
                  if (name === 'margin') return [`${value}%`, 'Margin'];
                  return [`$${(value / 1000000)?.toFixed(2)}M`, name];
                }}
              />
              <Legend />
              <Bar dataKey="profit" fill="var(--color-success)" radius={[8, 8, 0, 0]} name="Profit" />
              <Bar dataKey="margin" fill="var(--color-secondary)" radius={[8, 8, 0, 0]} name="Margin %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {profitByProgram?.map((program, index) => (
            <div key={index} className="p-3 rounded-md bg-muted/50">
              <p className="text-xs text-muted-foreground font-caption mb-2">{program?.program}</p>
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-caption">Profit</span>
                  <span className="text-sm font-semibold text-foreground font-data">${(program?.profit / 1000000)?.toFixed(2)}M</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-caption">Margin</span>
                  <span className="text-sm font-semibold text-success font-data">{program?.margin}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Profit Trend Over Time</h3>
        <div className="h-80 md:h-96" aria-label="Profit Trend Line Chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={profitTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              />
              <YAxis 
                yAxisId="left"
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000000)?.toFixed(1)}M`}
                label={{ value: 'Profit', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)' }}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
                label={{ value: 'Margin %', angle: 90, position: 'insideRight', fill: 'var(--color-muted-foreground)' }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-popover)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
                formatter={(value, name) => {
                  if (name === 'margin') return [`${value}%`, 'Margin'];
                  return [`$${(value / 1000000)?.toFixed(2)}M`, name];
                }}
              />
              <Legend />
              <Bar 
                yAxisId="left"
                dataKey="profit" 
                fill="var(--color-success)" 
                radius={[8, 8, 0, 0]}
                name="Actual Profit"
              />
              <Line 
                yAxisId="left"
                type="monotone" 
                dataKey="target" 
                stroke="var(--color-secondary)" 
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: 'var(--color-secondary)', r: 4 }}
                name="Target Profit"
              />
              <Line 
                yAxisId="right"
                type="monotone" 
                dataKey="margin" 
                stroke="var(--color-accent)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-accent)', r: 5 }}
                name="Profit Margin %"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Revenue vs Cost Comparison</h3>
        <div className="h-80 md:h-96" aria-label="Revenue vs Cost Comparison Chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueVsCost}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis 
                dataKey="month" 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
              />
              <YAxis 
                tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                tickFormatter={(value) => `$${(value / 1000000)?.toFixed(0)}M`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-popover)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
                formatter={(value) => [`$${(value / 1000000)?.toFixed(2)}M`, '']}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="var(--color-primary)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-primary)', r: 5 }}
                name="Revenue"
              />
              <Line 
                type="monotone" 
                dataKey="cost" 
                stroke="var(--color-error)" 
                strokeWidth={3}
                dot={{ fill: 'var(--color-error)', r: 5 }}
                name="Cost"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded-md bg-primary/10 border border-primary/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Current Revenue</p>
            <p className="text-xl font-heading font-semibold text-foreground font-data">$14.50M</p>
            <p className="text-xs text-success font-caption mt-1">+3.2% growth</p>
          </div>
          <div className="p-4 rounded-md bg-error/10 border border-error/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Current Cost</p>
            <p className="text-xl font-heading font-semibold text-foreground font-data">$8.75M</p>
            <p className="text-xs text-warning font-caption mt-1">+1.2% increase</p>
          </div>
          <div className="p-4 rounded-md bg-success/10 border border-success/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Gap Improvement</p>
            <p className="text-xl font-heading font-semibold text-foreground font-data">+2.0%</p>
            <p className="text-xs text-success font-caption mt-1">Widening favorably</p>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Profitability Insights & Recommendations</h3>
        <div className="space-y-4">
          {profitabilityInsights?.map((insight, index) => (
            <div 
              key={index} 
              className="p-4 rounded-md border"
              style={{ 
                backgroundColor: `${getImpactColor(insight?.impact)}10`,
                borderColor: `${getImpactColor(insight?.impact)}30`
              }}
            >
              <div className="flex items-start gap-3 mb-3">
                <Icon 
                  name={getImpactIcon(insight?.impact)} 
                  size={20} 
                  color={getImpactColor(insight?.impact)}
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-foreground">{insight?.insight}</h4>
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-semibold font-caption"
                      style={{ 
                        backgroundColor: `${getImpactColor(insight?.impact)}20`,
                        color: getImpactColor(insight?.impact)
                      }}
                    >
                      {insight?.impact}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mb-3 font-caption">{insight?.description}</p>
                  <div className="flex items-start gap-2 p-3 rounded bg-background">
                    <Icon name="Lightbulb" size={14} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-foreground font-caption">{insight?.recommendation}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfitDrivers;